/* =============================================================
   useLocalNote.js — Persistent admin notes with Supabase
   -------------------------------------------------------------
   Loads and saves a free-text note for any entity (student or
   partner) using the Supabase "notes" table.

   Each admin has one note per entity. The combination of
   (university_id, entity_key, created_by) is unique in the DB,
   so saving always does an upsert — no duplicate rows.

   Usage (unchanged from the localStorage version):
     const { note, setNote, save, savedAt, justSaved } =
       useLocalNote('incoming_<uuid>');

   entity_key format used in the screens:
     'incoming_<student-uuid>'   — GenericIncomingProfile
     'outgoing_<student-uuid>'   — GenericOutgoingProfile
     'partner_cbs'               — PartnerExchangeHistory (hardcoded)

   ✓ DONE (backend integration):
   - Removed localStorage read/write
   - Replaced with Supabase SELECT (load) + upsert (save)
   - university_id and created_by taken from AuthContext
   - save() is now async — UI callers use onClick={save}, which
     works fine with async functions in React
   ============================================================= */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth }  from '../context/AuthContext';


/* ─── formatSavedAt ──────────────────────────────────────────── */
// Converts an ISO timestamp to a human-readable string,
// e.g. "8 May at 14:32". Returns null if no timestamp yet.
function formatSavedAt(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return (
    d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) +
    ' at ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
}


/* ─── useLocalNote HOOK ──────────────────────────────────────── */
// key — unique string identifying what the note is about,
//        e.g. 'incoming_abc123' or 'partner_cbs'
export function useLocalNote(key) {
  const { user } = useAuth();

  const [note,      setNote]      = useState('');
  const [savedAt,   setSavedAt]   = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [saving,    setSaving]    = useState(false);

  // Track whether the initial load has completed so we don't
  // overwrite a note the user is typing while the fetch is in flight.
  const loaded = useRef(false);


  /* ── Load note on mount ──────────────────────────────────────── */
  // Fetches the admin's existing note for this entity from Supabase.
  // If no note exists yet, the textarea starts empty.
  useEffect(() => {
    if (!user?.id) return;

    async function loadNote() {
      // .maybeSingle() returns { data: null } when no row exists (no 406 error).
      // .single() would throw 406 if the note hasn't been saved yet.
      const { data } = await supabase
        .from('notes')
        .select('content, updated_at')
        .eq('entity_key',  key)
        .eq('created_by',  user.id)
        .maybeSingle();

      // .single() returns null data (not an error) if no row exists.
      if (data && !loaded.current) {
        setNote(data.content);
        setSavedAt(data.updated_at);
      }
      loaded.current = true;
    }

    loadNote();
  }, [key, user?.id]);


  /* ── save ────────────────────────────────────────────────────── */
  // Upserts the note to Supabase.
  // If a note for (university_id, entity_key, created_by) already
  // exists, it updates content + updated_at. Otherwise it inserts.
  // Shows "Saved!" feedback for 2 seconds after success.
  const save = async () => {
    if (!user?.id || !user?.universityId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('notes')
        .upsert(
          {
            university_id: user.universityId,
            entity_key:    key,
            content:       note,
            created_by:    user.id,
          },
          // Tell Supabase which columns form the unique key so it knows
          // whether to INSERT a new row or UPDATE the existing one.
          { onConflict: 'university_id,entity_key,created_by' }
        );

      if (!error) {
        const ts = new Date().toISOString();
        setSavedAt(ts);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      }
    } finally {
      setSaving(false);
    }
  };


  return {
    note,
    setNote,
    save,
    saving,
    savedAt:   formatSavedAt(savedAt),
    justSaved,
  };
}
